import { lazy, useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { RouterProvider } from 'react-router-dom';
import { getPlatformRef } from '@localagent/platform';
import { ThemeProvider } from './providers/ThemeProvider';
import { DensityProvider } from './providers/DensityProvider';
import { QueryProvider, queryClient } from './providers/QueryProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from '@localagent/ui/components/sonner';
import { installDesktopSyncNavigations, router } from './routes';
import { setSessionAction } from './stores/auth/atoms';
import { installSDKAction, sdkConfiguredAtom } from './stores/sdk/atoms';
import { getSDK } from './services/sdk-instance';
import { WebAuthSuspense } from './auth/WebAuthSuspense';
import { ShogunChopProvider } from './providers/ShogunChopProvider';
import { DesktopWindowChrome } from './components/layout/DesktopWindowChrome';
import { BootRefusalScreen } from './components/BootRefusalScreen';
import type { BootFailureCode } from './components/BootRefusalScreen';
import { DaemonStartingScreen } from './components/DaemonStartingScreen';
import {
  awaitLaunchSettled,
  readLaunchPhase,
  readLaunchRefusal,
  reconverge,
} from './boot/launch-bridge';
import { BootRefused, ALL_LAUNCH_REFUSAL_CODES } from '@localagent/sdk';
import type {
  BootRefusalCode,
  LaunchRefusalCode,
  LaunchStateName,
  ShogunChopResource,
} from '@localagent/sdk';

function isLaunchRefusalCode(value: unknown): value is LaunchRefusalCode {
  return (
    typeof value === 'string' && (ALL_LAUNCH_REFUSAL_CODES as readonly string[]).includes(value)
  );
}

/** A convergence failure is named by the launch machine, not the boot cause it surfaced as. */
async function namedBootFailure(code: BootRefusalCode): Promise<BootFailureCode> {
  return (await readLaunchRefusal()) ?? code;
}

/** Retry re-attempts the operation that failed, which for a convergence cause is convergence. */
async function retryBoot(failed: BootFailureCode | null): Promise<LaunchRefusalCode | null> {
  if (isLaunchRefusalCode(failed)) {
    const stillFailing = await reconverge();
    if (stillFailing !== null) return stillFailing;
  }
  await installSDKAction();
  return null;
}

/** Lazy so desktop never loads `@stytch/react`. */
const WebAuthRoot = lazy(() => import('./auth/WebAuthRoot'));

function App() {
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  const setSession = useSetAtom(setSessionAction);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [bootRefusal, setBootRefusal] = useState<BootFailureCode | null>(null);
  const [launchPhase, setLaunchPhase] = useState<LaunchStateName | null>(null);
  const [shogunchopResource, setShogunchopResource] = useState<ShogunChopResource | null>(null);

  useEffect(() => {
    if (!sdkConfigured) {
      setShogunchopResource(null);
      return;
    }
    try {
      const sdk = getSDK();
      setShogunchopResource(sdk.hasDaemon ? sdk.shogunchop : null);
    } catch {
      setShogunchopResource(null);
    }
  }, [sdkConfigured]);

  useEffect(() => {
    // Launch-only (`VITE_E2E_TEST_HELPERS`); `la release` asserts the symbol is absent.
    if (import.meta.env.VITE_E2E_TEST_HELPERS) {
      interface TestHelpers {
        /**
         * `claims` is accepted and IGNORED. The shell no longer takes claims
         * from a caller — the tenancy is read off the injected JWT by the same
         * `credentialAcquiredEvent` helper every production acquisition path
         * uses, so an injected session lands in exactly the phase a real one
         * would. A caller-supplied claims bag was how "a token with one org and
         * claims naming another" became representable.
         */
        setAuthToken: (
          token: string,
          claims?: Record<string, unknown>,
          session?: { sessionToken?: string | null; sessionJwt?: string | null },
        ) => Promise<void>;
        invalidateDeviceId: () => Promise<void>;
        getDeviceId: () => Promise<string | null>;
        isInitialized: () => boolean;
        getSDK: () => import('@localagent/sdk').LocalAgentSDK;
        queryClient: typeof queryClient;
      }
      (window as typeof window & { __E2E_TEST_HELPERS__?: TestHelpers }).__E2E_TEST_HELPERS__ = {
        setAuthToken: (
          token: string,
          _claims?: Record<string, unknown>,
          session?: { sessionToken?: string | null; sessionJwt?: string | null },
        ) => setSession({ token, sessionToken: session?.sessionToken ?? null }),
        invalidateDeviceId: async () => {
          getSDK().invalidateDeviceIdentity();
        },
        getDeviceId: async () => {
          const { getDeviceIdAsync } = await import('./services/sdk-instance');
          try {
            return await getDeviceIdAsync();
          } catch {
            return null;
          }
        },
        isInitialized: () => isInitialized,
        getSDK,
        queryClient,
      };
    }
  }, [isInitialized, setSession]);

  useEffect(() => {
    if (isInitialized || bootRefusal !== null || initError !== null) return;
    if (getPlatformRef().type !== 'desktop') return;
    let cancelled = false;
    const poll = () => {
      readLaunchPhase().then(
        (phase) => {
          if (!cancelled) setLaunchPhase(phase);
        },
        (err: unknown) => {
          // The phase line only decorates the wait; boot surfaces real failures (.claude/rules/no-fallbacks.md).
          console.warn('[App] launch phase unreadable; the startup screen stays generic', err);
          clearInterval(timer);
        },
      );
    };
    const timer = setInterval(poll, 500);
    poll();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isInitialized, bootRefusal, initError]);

  useEffect(() => {
    let cancelled = false;
    awaitLaunchSettled()
      .then(async (launchRefused) => {
        if (launchRefused !== null) {
          if (!cancelled) setBootRefusal(launchRefused);
          return false;
        }
        await installSDKAction();
        return true;
      })
      .then((installed) => {
        if (!installed) return;
        if (!cancelled) {
          setIsInitialized(true);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        if (error instanceof BootRefused) {
          // (sdk ADR-013, sdk ADR-016).
          namedBootFailure(error.code).then(
            (code) => {
              if (!cancelled) setBootRefusal(code);
            },
            (bridgeError: unknown) => {
              if (!cancelled) setInitError(String(bridgeError));
            },
          );
          return;
        }
        console.error('[App] FATAL: Failed to install the SDK:', error);
        const errorMsg =
          'Failed to initialize ShogunChop:\n\n' +
          String((error instanceof Error ? error.message : null) || error) +
          '\n\nThe application cannot start without a backend connection.';
        setInitError(errorMsg);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isDesktop = getPlatformRef().type === 'desktop';
  if (isDesktop) {
    installDesktopSyncNavigations();
  }

  if (bootRefusal !== null) {
    return (
      <DesktopWindowChrome>
        <BootRefusalScreen
          code={bootRefusal}
          onRetry={() => {
            const failed = bootRefusal;
            setBootRefusal(null);
            retryBoot(failed)
              .then((stillFailing) => {
                if (stillFailing !== null) setBootRefusal(stillFailing);
                else setIsInitialized(true);
              })
              .catch((err: unknown) => {
                if (err instanceof BootRefused) {
                  namedBootFailure(err.code).then(setBootRefusal, (bridgeError: unknown) =>
                    setInitError(String(bridgeError)),
                  );
                } else {
                  setInitError(String(err instanceof Error ? err.message : err));
                }
              });
          }}
        />
      </DesktopWindowChrome>
    );
  }

  if (!isInitialized || !sdkConfigured) {
    const initContent = (
      <div className="text-center">
        <div className="mb-4 text-2xl font-semibold">ShogunChop</div>
        {initError ? (
          <div className="max-w-md rounded-lg bg-red-50 p-4 text-sm text-red-900">
            <div className="mb-2 font-semibold">Initialization Failed</div>
            <div className="whitespace-pre-wrap text-left">{initError}</div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Initializing...</div>
        )}
      </div>
    );
    if (isDesktop && initError === null) {
      return (
        <DesktopWindowChrome>
          <DaemonStartingScreen phase={launchPhase} />
        </DesktopWindowChrome>
      );
    }
    if (isDesktop) {
      return (
        <DesktopWindowChrome>
          <div className="flex h-full min-h-full items-center justify-center bg-background">
            {initContent}
          </div>
        </DesktopWindowChrome>
      );
    }
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        {initContent}
      </div>
    );
  }

  const tree = (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="system" storageKey="localagent-theme">
        <DensityProvider>
          <QueryProvider>
            <ShogunChopProvider resource={shogunchopResource}>
              {isDesktop ? (
                // Router owns the shell. Desktop auth mirrors/overlays inside
                // MainLayout — never wraps RouterProvider (app-shell ADR-001).
                <RouterProvider router={router} />
              ) : (
                <WebAuthSuspense>
                  <WebAuthRoot>
                    <RouterProvider router={router} />
                  </WebAuthRoot>
                </WebAuthSuspense>
              )}
            </ShogunChopProvider>
            <Toaster />
          </QueryProvider>
        </DensityProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );

  return tree;
}

export default App;
